import { Trophy, Award, MessageSquareText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  useAnniversariesThisWeek,
  buildAnniversaryWaLink,
  anniversaryDayLabel,
} from "@/hooks/useAnniversariesThisWeek";

/**
 * Dashboard widget — persistent card listing work anniversaries (DOJ) in next 7 days.
 */
export default function AnniversaryWidget() {
  const { user } = useAuth();
  const { rows, count, eligible, loading } = useAnniversariesThisWeek(user);
  if (!eligible || loading || count === 0) return null;

  return (
    <div className="bg-white border border-amber-200 rounded-lg overflow-hidden shadow-sm" data-testid="anniv-widget">
      <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-transparent border-b border-amber-100 px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Trophy className="h-4 w-4 text-amber-700" />
          <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Work anniversaries
          </h3>
          <span className="bg-amber-100 text-amber-700 text-[10px] font-bold rounded-full px-2 py-0.5">
            {count}
          </span>
        </div>
        <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1">
          <Award className="h-3 w-3" /> Next 7 days
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {rows.map(emp => {
          const waLink = buildAnniversaryWaLink(emp);
          const isToday = emp.days_until === 0;
          const years = emp.years_completing;
          const isMilestone = years >= 5 && years % 5 === 0;
          return (
            <div key={emp.id} className={`px-4 py-2.5 flex items-center gap-3 ${isToday ? "bg-amber-50/40" : "hover:bg-slate-50"}`} data-testid={`anniv-widget-row-${emp.id}`}>
              <div className={`h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold ${isToday ? "bg-amber-500 text-white" : "bg-amber-100 text-amber-700"}`}>
                {emp.name?.[0]?.toUpperCase() || "?"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 flex items-center gap-1.5 flex-wrap">
                  {emp.name}
                  {isToday && <span className="text-[9px] bg-amber-500 text-white px-1 py-0.5 rounded font-bold">TODAY 🏆</span>}
                  <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${isMilestone ? "bg-orange-500 text-white" : "bg-amber-100 text-amber-700"}`}>
                    {years} {years === 1 ? "year" : "years"}{isMilestone && " · Milestone"}
                  </span>
                  <span className="text-[10px] text-slate-400 font-normal">· {emp.punch_id}</span>
                </div>
                <div className="text-[11px] text-slate-500 flex items-center gap-2">
                  <span className={isToday ? "text-amber-700 font-bold" : "text-slate-600"}>
                    {anniversaryDayLabel(emp.days_until)}
                  </span>
                  {emp.designation && <><span>·</span><span>{emp.designation}</span></>}
                  {emp.department_name && <><span>·</span><span>{emp.department_name}</span></>}
                </div>
              </div>
              {waLink ? (
                <a href={waLink} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white rounded transition-colors flex-shrink-0"
                    data-testid={`anniv-widget-wish-${emp.id}`}>
                  <MessageSquareText className="h-3.5 w-3.5" />
                  Appreciate
                </a>
              ) : (
                <span className="text-[10px] text-amber-600 italic" title="No phone on file">no phone</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
